import { cn } from '@/lib/utils';
import { formatEtDate } from '@/lib/cashFlow';
import type { CashFlowOffer } from '@/lib/cashFlowDetail';

/**
 * "Seotud pakkumine" — the linked Erply OFFER (pakkumine) row on the enriched
 * entry detail (module 1.3, Part A of the rich offer view). It surfaces the offer
 * an invoice stems from — number · date · party · supplier count — and opens the
 * full offer viewer (Part B) via `onOpen`. An offer is NOT a cash-flow entry, so
 * it opens its OWN viewer, never the entry drill (never `onOpenEntry`). Renders
 * nothing when the entry stems from no offer (an ORDER-based invoice). The
 * supplier grouping/money are owned by finance-core (master rule; L9) — this row
 * only COUNTS the groups it is handed; it computes no amount.
 */
export function OfferSummary({
  offer,
  onOpen,
  className,
}: {
  offer: CashFlowOffer | null;
  onOpen: () => void;
  className?: string;
}) {
  if (!offer) return null;
  const supplierCount = offer.groups.length;

  return (
    <section className={cn('rounded-lg border border-border bg-card/40', className)}>
      <header className="flex items-center gap-2 border-b border-border/70 px-3 py-2">
        <h3 className="text-[12px] font-semibold uppercase tracking-wide text-foreground">Seotud pakkumine</h3>
        <span className="ml-auto text-[10px] text-muted-foreground/70">pakkumine → arve</span>
      </header>
      <div className="px-3 py-3">
        <div className="flex items-center gap-2 rounded border border-[hsl(var(--primary))]/40 bg-[hsl(var(--primary))]/[0.06] px-2 py-1.5 text-[11px]">
          <span className="shrink-0 rounded border border-[hsl(var(--primary))]/50 px-1 text-[9px] font-semibold uppercase tracking-wide text-[hsl(var(--primary))]">
            Pakkumine
          </span>
          <span className="shrink-0 font-mono font-semibold text-foreground">{offer.docNumber ?? '—'}</span>
          {offer.documentDate && (
            <span className="shrink-0 text-muted-foreground">{formatEtDate(offer.documentDate)}</span>
          )}
          <span className="min-w-0 flex-1 truncate text-muted-foreground">
            · {offer.party}
            {supplierCount > 0 && ` · ${supplierCount} ${supplierCount === 1 ? 'tarnija' : 'tarnijat'}`}
          </span>
          <button
            type="button"
            onClick={onOpen}
            className="inline-flex shrink-0 items-center gap-1 font-semibold text-[hsl(var(--primary))] hover:underline"
          >
            Ava pakkumine
            <span aria-hidden>→</span>
          </button>
        </div>
      </div>
    </section>
  );
}
